/** Shared helpers for contract / license term fields. */

export const DEFAULT_DURATION_UNIT = 'DAYS';

function parseDate(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Compute end date from start + duration + unit. */
export function computeTermEndDate(startDate, duration, unit = DEFAULT_DURATION_UNIT) {
  if (!startDate || duration === '' || duration == null || !unit) return null;
  const amount = Number(duration);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const d = parseDate(startDate);
  if (!d) return null;
  switch (unit) {
    case 'DAYS':
      d.setDate(d.getDate() + amount);
      break;
    case 'WEEKS':
      d.setDate(d.getDate() + amount * 7);
      break;
    case 'MONTHS':
      d.setMonth(d.getMonth() + amount);
      break;
    case 'YEARS':
      d.setFullYear(d.getFullYear() + amount);
      break;
    default:
      return null;
  }
  return formatDate(d);
}

/** Compute duration from start + end according to unit (default DAYS). */
export function computeTermDuration(startDate, endDate, unit = DEFAULT_DURATION_UNIT) {
  const s = parseDate(startDate);
  const e = parseDate(endDate);
  if (!s || !e || e < s || !unit) return null;
  const msPerDay = 86400000;
  switch (unit) {
    case 'DAYS':
      return Math.round((e - s) / msPerDay);
    case 'WEEKS':
      return Math.round((e - s) / (msPerDay * 7));
    case 'MONTHS': {
      let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
      if (e.getDate() < s.getDate()) months -= 1;
      return Math.max(0, months);
    }
    case 'YEARS': {
      let years = e.getFullYear() - s.getFullYear();
      const beforeAnniversary =
        e.getMonth() < s.getMonth()
        || (e.getMonth() === s.getMonth() && e.getDate() < s.getDate());
      if (beforeAnniversary) years -= 1;
      return Math.max(0, years);
    }
    default:
      return null;
  }
}

/**
 * Apply a field change for a term prefix (`contract` | `license`).
 * - Dates entered → duration auto-calculated using unit (defaults to DAYS).
 * - Start + duration (+ unit) → end date auto-calculated.
 */
export function applyTermFieldChange(values, prefix, name, value, setField) {
  setField(name, value);

  const startKey = `${prefix}StartDate`;
  const endKey = `${prefix}EndDate`;
  const durationKey = `${prefix}Duration`;
  const unitKey = `${prefix}DurationUnit`;

  const next = {
    [startKey]: values[startKey],
    [endKey]: values[endKey],
    [durationKey]: values[durationKey],
    [unitKey]: values[unitKey] || DEFAULT_DURATION_UNIT,
    [name]: value,
  };

  if (!next[unitKey]) {
    next[unitKey] = DEFAULT_DURATION_UNIT;
    setField(unitKey, DEFAULT_DURATION_UNIT);
  }

  const unit = next[unitKey] || DEFAULT_DURATION_UNIT;
  const touchedDateOrUnit =
    name === startKey || name === endKey || name === unitKey;

  if (touchedDateOrUnit && next[startKey] && next[endKey]) {
    const duration = computeTermDuration(next[startKey], next[endKey], unit);
    if (duration != null) setField(durationKey, duration);
    return;
  }

  if (
    (name === startKey || name === durationKey || name === unitKey)
    && next[startKey]
    && next[durationKey] !== ''
    && next[durationKey] != null
  ) {
    const end = computeTermEndDate(next[startKey], next[durationKey], unit);
    if (end) setField(endKey, end);
  }
}
